import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n, langPath } from "@/i18n/I18nContext";
import { Mail, MapPin, Phone, ArrowUpRight, Building2 } from "lucide-react";

export default function Footer({ onOpenCookiePrefs }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  return (
    <footer className="mt-24 pb-10" style={{ background: "var(--z-bg-2)" }} id="about">
      <div className="z-container pt-16">
        {/* Newsletter block */}
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-10 items-end mb-14">
          <div>
            <div className="z-chip mb-4"><span className="dot"/>Zynteroq</div>
            <h3 className="font-display text-[32px] md:text-[40px] leading-[1.05] max-w-[520px]">
              {t("footer.newsletterTitle")}
            </h3>
            <p className="mt-3 text-[15px] text-[color:var(--z-muted)] max-w-[520px]">
              {t("footer.newsletterText")}
            </p>
          </div>
          <div className="flex md:justify-end">
            <Link
              to={langPath(lang) + "#contact"}
              className="z-btn z-btn-primary"
              data-testid="footer-newsletter-cta"
            >
              {t("footer.newsletterCta")} <ArrowUpRight size={16}/>
            </Link>
          </div>
        </div>

        <div className="z-hairline mb-12"/>

        {/* Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <Link to={langPath(lang)} className="flex items-center gap-2">
              <span aria-hidden className="inline-flex items-center justify-center w-9 h-9 rounded-xl" style={{ background: "linear-gradient(135deg,#0F3B36,#0B6B5A)" }}>
                <Building2 size={18} color="#E7C989" />
              </span>
              <span className="font-display text-[22px]">Zynteroq</span>
            </Link>
            <p className="mt-4 text-[14px] text-[color:var(--z-muted)] max-w-[260px]">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="text-[12px] font-semibold uppercase tracking-wider text-[color:var(--z-muted)] mb-4">
              {t("footer.company")}
            </h4>
            <ul className="space-y-3 text-[14px]">
              <li><a href={langPath(lang) + "#how"} data-testid="fnav-how">{t("nav.how")}</a></li>
              <li><a href={langPath(lang) + "#projects"} data-testid="fnav-projects">{t("nav.projects")}</a></li>
              <li><a href={langPath(lang) + "#about"} data-testid="fnav-about">{t("nav.about")}</a></li>
              <li><a href={langPath(lang) + "#contact"} data-testid="fnav-contact">{t("nav.contact")}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[12px] font-semibold uppercase tracking-wider text-[color:var(--z-muted)] mb-4">
              {t("footer.legal")}
            </h4>
            <ul className="space-y-3 text-[14px]">
              <li><Link to={langPath(lang, "/privacy")} data-testid="flegal-privacy">{t("nav.privacy")}</Link></li>
              <li><Link to={langPath(lang, "/cookies")} data-testid="flegal-cookies">{t("nav.cookies")}</Link></li>
              <li><Link to={langPath(lang, "/terms")} data-testid="flegal-terms">{t("nav.terms")}</Link></li>
              <li>
                <button
                  type="button"
                  onClick={onOpenCookiePrefs}
                  className="underline underline-offset-4 hover:text-[color:var(--z-brand)]"
                  data-testid="open-cookie-prefs"
                >
                  {lang === "hr" ? "Postavke privatnosti" : "Privacy preferences"}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-[12px] font-semibold uppercase tracking-wider text-[color:var(--z-muted)] mb-4">
              {t("footer.resources")}
            </h4>
            <ul className="space-y-3 text-[14px]">
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 opacity-60"/>
                <span>{t("footer.address")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={14} className="opacity-60"/>
                <a href="tel:+38516345720" data-testid="phone-link">+385 1 6345 720</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} className="opacity-60"/>
                <a href="mailto:hello@zynteroq.com" data-testid="email-link">hello@zynteroq.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="z-hairline my-10"/>

        <p className="text-[12px] leading-relaxed text-[color:var(--z-muted)]">
          {t("footer.legalDetails")}
        </p>
        <p className="mt-6 text-[12px] text-[color:var(--z-muted)]">
          © {year} Zynteroq d.o.o. — {t("footer.rights")}
        </p>
      </div>
    </footer>
  );
}
